import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, errMsg, today, METHODS, fmtMoney } from "@/lib/api";
import { Field, inputCls, selectCls, textareaCls } from "@/components/common";

export function PaymentDialog({ open, onClose, invoice = null, onSaved, preset = null }) {
  const qc = useQueryClient();
  const manual = !invoice;
  const { data: customers = [] } = useQuery({ queryKey: ["customers"], queryFn: () => api.get("/customers").then((r) => r.data), enabled: open && manual });
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm({ amount: invoice ? invoice.balance_due : "", method: invoice ? (preset?.method || "card") : "cash", reference: preset?.reference || "", date: today(), notes: preset?.notes || "", description: "", customer_id: "" });
  }, [open, invoice, preset]);
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Enter an amount");
    setSaving(true);
    try {
      const body = { ...form, amount: Number(form.amount), invoice_id: invoice?.id || null, customer_id: form.customer_id || null };
      const { data } = await api.post("/payments", body);
      toast.success(manual ? "Income recorded" : "Payment recorded. No receipt was sent — use Send Receipt if the customer wants one.");
      qc.invalidateQueries();
      onSaved?.(data);
      onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="payment-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{manual ? "Manual Income" : preset?.method === "venmo" ? "Venmo Payment Received" : "Record Payment"}</DialogTitle></DialogHeader>
        {preset?.method === "venmo" && invoice && <p className="text-sm text-slate-600 -mt-2" data-testid="venmo-paid-hint">Confirm the amount that arrived in Venmo{invoice.venmo_username ? ` (@${invoice.venmo_username})` : ""} and add the Venmo transaction note or ID as the reference.</p>}
        {invoice && <div className="rounded-lg bg-slate-50 border p-3 text-sm flex justify-between"><span>Invoice {invoice.number}</span><span className="font-semibold">Balance {fmtMoney(invoice.balance_due)}</span></div>}
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount"><input data-testid="payment-amount-input" type="number" step="0.01" min="0" inputMode="decimal" className={inputCls + " text-lg font-semibold"} value={form.amount ?? ""} onChange={set("amount")} required /></Field>
            <Field label="Date"><input data-testid="payment-date-input" type="date" className={inputCls} value={form.date || ""} onChange={set("date")} /></Field>
          </div>
          <Field label="Method">
            <select data-testid="payment-method-select" className={selectCls} value={form.method || ""} onChange={set("method")}>
              {METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            {form.method === "card" && <p className="text-xs text-slate-500 mt-1" data-testid="external-card-hint">Use this only for a card payment you already collected through your own card reader or processor. NEEC Service does not charge cards.</p>}
          </Field>
          <Field label="Reference (optional)"><input data-testid="payment-reference-input" className={inputCls} value={form.reference || ""} onChange={set("reference")} placeholder="Check #, Venmo handle, last 4..." /></Field>
          {manual && (
            <>
              <Field label="Customer (optional)">
                <select data-testid="payment-customer-select" className={selectCls} value={form.customer_id || ""} onChange={set("customer_id")}>
                  <option value="">No customer</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Description"><input data-testid="payment-description-input" className={inputCls} value={form.description || ""} onChange={set("description")} placeholder="Cash job, tip, etc." /></Field>
            </>
          )}
          <Field label="Notes"><textarea data-testid="payment-notes-input" className={textareaCls + " min-h-[60px]"} value={form.notes || ""} onChange={set("notes")} /></Field>
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" className="flex-1 h-11" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="flex-1 h-11 btn-orange" data-testid="payment-save-button">{saving ? "Saving..." : manual ? "Record Income" : "Record Payment"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
